/**
 * The one half of the accent engine that touches a page: set the custom
 * properties `resolveAccent` computed (`accent.ts`, pure maths) on an
 * element, usually <html>. Inline custom properties beat every stylesheet,
 * layered or not, so they win over `theme.css` and over a game's own
 * unlayered mapping alike.
 *
 * A game with tokens of its own passes its own names: `applyAccent` sets
 * whatever `--*` names the record holds (TwinTen maps the result onto its
 * `--tt-*` tokens and sets those).
 *
 * Since 1.4.0 it also keeps the resolved accent for the first frame:
 * `storeAccentCache` and `readAccentCache` here, and `accent-boot.js`, which
 * sets the cached tokens before the bundle runs.
 */
import type { AccentTokens } from './accent.js';

/** Set every `--name: value` of `tokens` on `target`'s inline style. */
export function applyAccent(
  target: HTMLElement,
  tokens: AccentTokens | Readonly<Record<string, string>>,
): void {
  for (const [name, value] of Object.entries(tokens)) {
    if (name.startsWith('--')) target.style.setProperty(name, value);
  }
}

/** Remove the named properties from `target`'s inline style: back to the stylesheets. */
export function clearAccent(target: HTMLElement, names: Iterable<string>): void {
  for (const name of names) target.style.removeProperty(name);
}

/* ------------------------------------------------------------ the cache */

/** The cache record's shape version; `accent-boot.js` reads only this one. */
export const ACCENT_CACHE_VERSION = 1;

/**
 * The resolved accent, cached for the first frame (since 1.4.0): the colour
 * the player chose and the tokens it resolved to on each base, so
 * `accent-boot.js` can set them before the bundle runs.
 */
export interface AccentCache {
  readonly hex: string;
  readonly dark: Readonly<Record<string, string>>;
  readonly light: Readonly<Record<string, string>>;
}

const TOKEN_NAME = /^--[a-z0-9-]+$/i;
const FLAT_HEX = /^#[0-9a-f]{6}$/i;

/** Only `--name: #rrggbb` pairs survive, so a cache can never inject anything else. */
function cleanTokens(tokens: unknown): Record<string, string> | null {
  if (typeof tokens !== 'object' || tokens === null) return null;
  const out: Record<string, string> = {};
  for (const [name, value] of Object.entries(tokens)) {
    if (TOKEN_NAME.test(name) && typeof value === 'string' && FLAT_HEX.test(value)) {
      out[name] = value.toLowerCase();
    }
  }
  return out;
}

/**
 * Remember `hex` and its resolved tokens on both bases under `key`, for
 * `accent-boot.js` (the same key on its `data-key`). Call it whenever the
 * accent is saved, with `resolveAccent(hex, 'dark').tokens` and
 * `resolveAccent(hex, 'light').tokens` (or a game's own names). A refused
 * write is not an error: the first frame then shows the stylesheet's accent.
 */
export function storeAccentCache(
  key: string,
  hex: string,
  tokens: {
    readonly dark: Readonly<Record<string, string>>;
    readonly light: Readonly<Record<string, string>>;
  },
): void {
  const dark = cleanTokens(tokens.dark) ?? {};
  const light = cleanTokens(tokens.light) ?? {};
  try {
    globalThis.localStorage?.setItem(
      key,
      JSON.stringify({ v: ACCENT_CACHE_VERSION, hex: hex.toLowerCase(), dark, light }),
    );
  } catch {
    // Private mode or a full quota.
  }
}

/** The cache under `key`, or `null` when there is none, it is unreadable, or of another version. */
export function readAccentCache(key: string): AccentCache | null {
  try {
    const raw = globalThis.localStorage?.getItem(key);
    if (raw === null || raw === undefined) return null;
    const record = JSON.parse(raw) as Record<string, unknown>;
    if (record['v'] !== ACCENT_CACHE_VERSION || typeof record['hex'] !== 'string') return null;
    const dark = cleanTokens(record['dark']);
    const light = cleanTokens(record['light']);
    if (dark === null || light === null) return null;
    return { hex: record['hex'], dark, light };
  } catch {
    return null;
  }
}
